import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Calendar, Clock, MapPin, MessageSquare, CheckCircle, XCircle, AlertCircle, Eye } from 'lucide-react';
import OutingNavigation from './OutingNavigation';

const ParentOutingPortal = () => {
  const { user } = useAuth();
  const [outingRequests, setOutingRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showPermissionModal, setShowPermissionModal] = useState(false);

  // Permission form state
  const [permissionData, setPermissionData] = useState({
    isGranted: false,
    pickupDate: '',
    pickupTime: '',
    dropDate: '',
    dropTime: '',
    message: ''
  });

  useEffect(() => {
    if (user) {
      fetchOutingRequests();
    }
  }, [user]);

  const fetchOutingRequests = async () => {
    try {
      setLoading(true);
      const response = await fetch(`/api/outing/requests?parentId=${user._id}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setOutingRequests(data.outingRequests || []);
      }
    } catch (error) {
      console.error('Error fetching outing requests:', error);
      setError('Failed to fetch outing requests');
    } finally {
      setLoading(false);
    }
  };

  const handleGrantPermission = async () => {
    if (!selectedRequest) return;

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`/api/outing/request/${selectedRequest._id}/permission`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(permissionData)
      });

      if (response.ok) {
        const result = await response.json();
        // Update the request in the list
        setOutingRequests(prev => 
          prev.map(req => 
            req._id === selectedRequest._id ? result.outingRequest : req
          )
        );
        setShowPermissionModal(false);
        setSelectedRequest(null);
        setPermissionData({
          isGranted: false,
          pickupDate: '',
          pickupTime: '',
          dropDate: '',
          dropTime: '',
          message: ''
        });
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Failed to grant permission');
      }
    } catch (error) {
      console.error('Error granting permission:', error);
      setError('Failed to grant permission');
    } finally {
      setLoading(false);
    }
  };

  const openPermissionModal = (request) => {
    setSelectedRequest(request);
    setPermissionData({
      isGranted: true,
      pickupDate: request.outingDate,
      pickupTime: request.departureTime,
      dropDate: request.returnDate,
      dropTime: request.returnTime,
      message: ''
    });
    setShowPermissionModal(true);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'class_teacher_approved': return 'bg-blue-100 text-blue-800';
      case 'warden_approved': return 'bg-indigo-100 text-indigo-800';
      case 'principal_approved': return 'bg-green-100 text-green-800';
      case 'approved': return 'bg-green-100 text-green-800';
      case 'denied': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'pending': return 'Pending Your Permission';
      case 'class_teacher_approved': return 'Class Teacher Approved';
      case 'warden_approved': return 'Warden Approved';
      case 'principal_approved': return 'Principal Approved';
      case 'approved': return 'Approved';
      case 'denied': return 'Denied';
      default: return 'Unknown';
    }
  };

  const canGrantPermission = (request) => {
    return request.approvalStatus === 'pending' && !request.parentPermission?.isGranted;
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation */}
      <OutingNavigation userRole="parent" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Message */}
        <div className="mb-6">
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center space-x-3">
              <Calendar className="h-6 w-6 text-green-600" />
              <div>
                <h3 className="text-lg font-medium text-green-900">
                  Welcome, {user.profile?.firstName || 'Parent'}!
                </h3>
                <p className="text-green-700">
                  Monitor your child's outing requests and grant permissions here.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Page Header */}
        <div className="mb-8">
          <div>
            <h2 className="text-3xl font-bold text-gray-900 mb-2">Outing Management</h2>
            <p className="text-gray-600">Monitor and grant permission for your child's outing requests</p>
          </div>
        </div>

        {/* Outing Requests List */}
        <div className="bg-white rounded-lg shadow-md">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Outing Requests</h3>
          </div>

          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading outing requests...</p>
            </div>
          ) : outingRequests.length === 0 ? (
            <div className="p-8 text-center">
              <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">No outing requests found</p>
              <p className="text-sm text-gray-500">Your child's outing requests will appear here</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {outingRequests.map((request) => (
                <div key={request._id} className="p-6 hover:bg-gray-50">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-3">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(request.approvalStatus)}`}>
                          {getStatusText(request.approvalStatus)}
                        </span>
                        <span className="text-sm text-gray-500">
                          Request ID: {request.requestId}
                        </span>
                        {request.parentPermission?.isGranted && (
                          <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                            Permission Granted
                          </span>
                        )}
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                        <div className="flex items-center space-x-2">
                          <Calendar className="h-4 w-4 text-gray-400" />
                          <span className="text-sm text-gray-600">
                            {new Date(request.outingDate).toLocaleDateString()}
                          </span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Clock className="h-4 w-4 text-gray-400" />
                          <span className="text-sm text-gray-600">
                            {request.departureTime} - {request.returnTime}
                          </span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <MapPin className="h-4 w-4 text-gray-400" />
                          <span className="text-sm text-gray-600">
                            {request.numberOfDays} day{request.numberOfDays > 1 ? 's' : ''}
                          </span>
                        </div>
                      </div>

                      {request.studentMessage && (
                        <div className="flex items-start space-x-2 mb-3">
                          <MessageSquare className="h-4 w-4 text-gray-400 mt-0.5" />
                          <p className="text-sm text-gray-600">{request.studentMessage}</p>
                        </div>
                      )}

                      {/* Parent Permission Details */}
                      {request.parentPermission?.isGranted && (
                        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-3">
                          <h4 className="text-sm font-medium text-green-800 mb-2">Permission Details:</h4>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                            <div>
                              <span className="text-green-700 font-medium">Pickup:</span>
                              <span className="text-green-600 ml-2">
                                {new Date(request.parentPermission.pickupDate).toLocaleDateString()} at {request.parentPermission.pickupTime}
                              </span>
                            </div>
                            <div>
                              <span className="text-green-700 font-medium">Drop:</span>
                              <span className="text-green-600 ml-2">
                                {new Date(request.parentPermission.dropDate).toLocaleDateString()} at {request.parentPermission.dropTime}
                              </span>
                            </div>
                            {request.parentPermission.message && (
                              <div className="col-span-2">
                                <span className="text-green-700 font-medium">Message:</span>
                                <span className="text-green-600 ml-2">{request.parentPermission.message}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Approval Flow Status */}
                      <div className="mt-4">
                        <h4 className="text-sm font-medium text-gray-700 mb-2">Approval Status:</h4>
                        <div className="flex space-x-4">
                          <div className="flex items-center space-x-2">
                            <div className={`w-3 h-3 rounded-full ${
                              request.approvalFlow?.classTeacher?.status === 'approved' ? 'bg-green-500' : 
                              request.approvalFlow?.classTeacher?.status === 'denied' ? 'bg-red-500' : 'bg-gray-300'
                            }`}></div>
                            <span className="text-xs text-gray-600">Class Teacher</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <div className={`w-3 h-3 rounded-full ${
                              request.approvalFlow?.warden?.status === 'approved' ? 'bg-green-500' : 
                              request.approvalFlow?.warden?.status === 'denied' ? 'bg-red-500' : 'bg-gray-300'
                            }`}></div>
                            <span className="text-xs text-gray-600">Warden</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <div className={`w-3 h-3 rounded-full ${
                              request.approvalFlow?.principal?.status === 'approved' ? 'bg-green-500' : 
                              request.approvalFlow?.principal?.status === 'denied' ? 'bg-red-500' : 'bg-gray-300'
                            }`}></div>
                            <span className="text-xs text-gray-600">Principal</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center space-x-2">
                      {canGrantPermission(request) && (
                        <button
                          onClick={() => openPermissionModal(request)}
                          className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors text-sm"
                        >
                          Grant Permission
                        </button>
                      )}
                      <button
                        onClick={() => {/* TODO: View details modal */}}
                        className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                        title="View Details"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Permission Modal */}
      {showPermissionModal && selectedRequest && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Grant Permission</h3>
              <p className="text-sm text-gray-600">Approve your child's outing request</p>
            </div>

            <div className="px-6 py-4">
              <form className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Pickup Date
                    </label>
                    <input
                      type="date"
                      value={permissionData.pickupDate}
                      onChange={(e) => setPermissionData({...permissionData, pickupDate: e.target.value})}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Pickup Time
                    </label>
                    <input
                      type="time"
                      value={permissionData.pickupTime}
                      onChange={(e) => setPermissionData({...permissionData, pickupTime: e.target.value})}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Drop Date
                    </label>
                    <input
                      type="date"
                      value={permissionData.dropDate}
                      onChange={(e) => setPermissionData({...permissionData, dropDate: e.target.value})}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Drop Time
                    </label>
                    <input
                      type="time"
                      value={permissionData.dropTime}
                      onChange={(e) => setPermissionData({...permissionData, dropTime: e.target.value})}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Message (Optional)
                  </label>
                  <textarea
                    value={permissionData.message}
                    onChange={(e) => setPermissionData({...permissionData, message: e.target.value})}
                    rows="3"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500"
                    placeholder="Any additional instructions or messages..."
                  />
                </div>

                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                    <p className="text-red-800 text-sm">{error}</p>
                  </div>
                )}

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowPermissionModal(false)}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleGrantPermission}
                    disabled={loading}
                    className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-50 flex items-center"
                  >
                    {loading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Granting...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="h-4 w-4 mr-2" />
                        Grant Permission
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ParentOutingPortal;
